import { ModelInfo } from "@jammable/clientapi";
import { Check } from "lucide-react";
import { VoiceImage } from "../../utils";
import { UpsellCard, useCoverUpsell } from "../Upsell";

// Pipeline stages, in order, in plain English. pending_source / advancing
// mean the source track is itself being generated first, so that stage is
// only listed for those covers. The statuses are the old pipeline's: the
// jammer leaves a cover "pending" throughout and reports how far it has got
// as `stage` (1-4, the index into STAGES; see the v2 webhook's stageOf).
const GENERATE_STAGE = {
  name: "Making the song",
  statuses: ["pending_source", "advancing"],
};
const STAGES = [
  { name: "Waiting in queue", statuses: ["waiting", "pending"] },
  { name: "Separating the vocals", statuses: ["extracting"] },
  { name: "Setting up the voices", statuses: ["cleaning"] },
  { name: "Singing it in their voices", statuses: ["converting"] },
  { name: "Final touches", statuses: ["mixing"] },
];

const RING = 300;
const STROKE = 14;
const RADIUS = (RING - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function Loader({
  status,
  progress,
  stage,
  models,
  artistNames,
  isAuthor,
  conversionId,
  subscriptions,
}: {
  acapellaStatus?: string;
  modelStatus?: string;
  status: string;
  progress?: number;
  stage?: number;
  models?: Partial<ModelInfo>[];
  artistNames?: string;
  isAuthor?: boolean;
  conversionId?: string;
  // The signed-in owner's, for the "While you wait" upsell.
  subscriptions?: any;
}) {
  const displayProgress =
    progress !== undefined && progress !== null && progress > 0;
  const currentProgressPercent = displayProgress
    ? Math.min(100, Math.max(0, progress || 0))
    : 0;

  const generating = GENERATE_STAGE.statuses.includes(status);
  const stages = generating ? [GENERATE_STAGE, ...STAGES] : STAGES;
  let current = stages.findIndex((s) => s.statuses.includes(status));
  if (current < 0) current = 0;
  if (!generating && stage)
    current = Math.max(current, Math.min(stage, STAGES.length - 1));
  // Progress means the job has left the queue even if the status still
  // reads pending.
  if (!generating && displayProgress && current === 0) current = 1;

  // Every voice in the cover (up to six), sized to stay inside the ring;
  // five or six sit on two rows.
  const voices = (models || []).slice(0, 6);
  const n = voices.length;
  const size =
    n <= 2
      ? "tw-h-[96px] tw-w-[96px] md:tw-h-[120px] md:tw-w-[120px]"
      : n === 3
        ? "tw-h-[80px] tw-w-[80px] md:tw-h-[96px] md:tw-w-[96px]"
        : n === 4
          ? "tw-h-[64px] tw-w-[64px] md:tw-h-[76px] md:tw-w-[76px]"
          : "tw-h-[58px] tw-w-[58px] md:tw-h-[68px] md:tw-w-[68px]";
  const overlap = n <= 2 ? -30 : n === 3 ? -24 : -18;
  const rows = n >= 5 ? [voices.slice(0, 3), voices.slice(3)] : [voices];
  const plural = (models?.length || 0) > 1;
  // Three or more full voice names ran to six lines; name two, count the rest.
  const names = (models || [])
    .map((m) => m?.artistname?.trim())
    .filter(Boolean);
  const who =
    names.length > 2
      ? `${names.slice(0, 2).join(", ")} & ${names.length - 2} more`
      : artistNames;
  // "making", not "learning the song": the voices don't learn anything, and
  // the audio is often not a song (typed words, a recording).
  const upsell = useCoverUpsell({
    placement: "loader",
    conversionId,
    subscriptions,
    enabled: isAuthor,
  });

  const title = who
    ? `${who} ${plural ? "are" : "is"} making ${isAuthor ? "your" : "this"} cover`
    : "Making the cover";

  return (
    <section
      className={`tw-grid tw-gap-6 md:tw-grid-cols-2 md:tw-items-center md:tw-gap-10 ${upsell.card ? "tw-items-start" : "tw-items-center"}`}
    >
      {/* Shorter on phones when the upsell card is under it, so the card
          isn't pushed below the fold. */}
      <div
        className={`tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-[34px] tw-bg-jam-lav md:tw-h-[560px] md:tw-rounded-[44px] ${upsell.card ? "tw-h-[240px]" : "tw-h-[340px]"}`}
      >
        <svg
          width={RING}
          height={RING}
          viewBox={`0 0 ${RING} ${RING}`}
          className={`tw-absolute md:tw-h-[300px] md:tw-w-[300px] ${upsell.card ? "tw-h-[200px] tw-w-[200px]" : "tw-h-[260px] tw-w-[260px]"} ${displayProgress ? "" : "tw-animate-spin [animation-duration:2.4s]"}`}
          style={{ transform: displayProgress ? "rotate(-90deg)" : undefined }}
          aria-hidden
        >
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke="#E3D3F7"
            strokeWidth={STROKE}
          />
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke="#782DC8"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={
              displayProgress
                ? CIRCUMFERENCE * (1 - currentProgressPercent / 100)
                : CIRCUMFERENCE * 0.75
            }
            style={{ transition: "stroke-dashoffset 0.3s ease-out" }}
          />
        </svg>
        <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-1">
          {rows.map((row, r) => (
            <div key={r} className="tw-flex">
              {row.map((m, i) => (
                <span
                  key={m._id || i}
                  style={{ marginLeft: i ? overlap : 0 }}
                  className={`tw-relative tw-overflow-hidden tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-bg-jam-line ${size}`}
                >
                  <VoiceImage
                    model={m}
                    fill
                    sizes="120px"
                    className="tw-object-cover"
                  />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
        {upsell.card ? (
          <>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {title}
            </h1>
            <CompactProgress
              stages={stages.map((s) => s.name)}
              current={current}
              percent={displayProgress ? currentProgressPercent : null}
            />
            <UpsellCard
              card={upsell.card}
              placement="loader"
              voice={voices[0]}
              yearlyPreview={upsell.yearlyPreview}
              onDismiss={upsell.dismiss}
            />
          </>
        ) : (
          <>
            <span className="tw-self-start tw-rounded-full tw-bg-[#FFF6CC] tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-text-[#7A6400]">
              {displayProgress
                ? `${Math.round(currentProgressPercent)}% done`
                : stages[current].name}
            </span>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {title}
            </h1>
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {stages.map((stage, i) => {
                const done = i < current;
                const now = i === current;
                return (
                  <li
                    key={stage.name}
                    className={`tw-flex tw-items-center tw-gap-3.5 tw-rounded-[20px] tw-border-2 tw-border-solid tw-px-4 tw-py-3 ${
                      now
                        ? "tw-border-jam-purple tw-bg-white"
                        : done
                          ? "tw-border-jam-lav tw-bg-jam-lav"
                          : "tw-border-jam-line tw-bg-white"
                    }`}
                  >
                    <span
                      className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-[3px] tw-border-solid ${
                        done
                          ? "tw-border-jam-lime tw-bg-jam-lime"
                          : now
                            ? "tw-border-jam-purple tw-bg-white"
                            : "tw-border-jam-line tw-bg-white"
                      }`}
                    >
                      {done && (
                        <Check
                          size={14}
                          strokeWidth={3}
                          className="tw-text-jam-ink"
                        />
                      )}
                    </span>
                    <span
                      className={`tw-flex-grow tw-text-[15px] tw-font-extrabold ${done || now ? "tw-text-jam-ink" : "tw-text-jam-muted"}`}
                    >
                      {stage.name}
                    </span>
                    <span className="tw-text-xs tw-font-extrabold tw-text-jam-muted">
                      {done ? "done" : now ? "now" : ""}
                    </span>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </section>
  );
}

// The stage list folded into one bar, to make room for the upsell card: a
// segment per stage, lime when done, the current one filled to its share.
function CompactProgress({
  stages,
  current,
  percent,
}: {
  stages: string[];
  current: number;
  percent: number | null;
}) {
  // Progress is for the whole job; how far into the current stage is a guess
  // from it, kept between a sliver and most of the segment.
  const share = stages.length ? 100 / stages.length : 100;
  const inStage =
    percent === null
      ? 50
      : Math.min(85, Math.max(15, ((percent - current * share) / share) * 100));
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div
        className="tw-grid tw-gap-[5px]"
        style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}
      >
        {stages.map((name, i) => (
          <span
            key={name}
            className={`tw-h-2 tw-rounded-full ${i < current ? "tw-bg-jam-lime" : "tw-bg-jam-line"}`}
            style={
              i === current
                ? {
                    background: `linear-gradient(90deg, #782DC8 ${inStage}%, #E9DEF8 ${inStage}%)`,
                  }
                : undefined
            }
          />
        ))}
      </div>
      <div className="tw-flex tw-justify-between tw-gap-3 tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
        <span className="tw-truncate tw-text-jam-ink">{stages[current]}</span>
        <span className="tw-shrink-0">
          {percent !== null ? `${Math.round(percent)}% · ` : ""}step{" "}
          {current + 1} of {stages.length}
        </span>
      </div>
    </div>
  );
}
